import { isAxiosError } from 'axios'

import { toApiError } from './api-error'

/**
 * Handing a file the API answered with to the browser.
 *
 * A binary endpoint cannot go through `mainApi`: the shared JSON client decodes
 * the body as text and the bytes are already lost by the time the caller sees
 * them. So each one asks for `responseType: 'blob'` off `mainHttp` and then has
 * the same two problems — turning the blob into a download, and reading an
 * error that arrived as a blob rather than as JSON.
 *
 * `features/orders` and `features/tables` each solved both inline before this
 * file existed. They still carry their own copies; anything new should use
 * these so there is not a fifth.
 */

/**
 * Saves a blob under `fileName`.
 *
 * The object URL is released on the next tick rather than immediately: revoking
 * synchronously races the download in some browsers and writes an empty file.
 */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = url
  link.download = fileName
  document.body.append(link)
  link.click()
  link.remove()

  setTimeout(() => URL.revokeObjectURL(url), 0)
}

/**
 * An error off a blob request, with the server's words recovered.
 *
 * A failed request for a blob still carries a JSON body, but axios has already
 * wrapped it as a Blob because that is what the caller asked for — so
 * `toApiError` would find nothing readable and the merchant would get the
 * generic fallback instead of "No file was uploaded". Reading the blob back as
 * text puts the real message in front of them.
 */
export async function blobError(error: unknown): Promise<unknown> {
  if (!isAxiosError(error) || !(error.response?.data instanceof Blob)) return toApiError(error)

  try {
    error.response.data = JSON.parse(await error.response.data.text())
  } catch {
    // Not JSON, or unreadable. Fall through with the original — the generic
    // message is still better than throwing while reporting a throw.
  }

  return toApiError(error)
}
